/**
 * What the person actually looks at before pinning a thumb (ADR-010 §8).
 *
 * The analyzer's own classification is NOT it. Measured on the golden android,
 * the same word means opposite things: `hand-5` at R@40 is three digits and two
 * bumps (persistence 22,13,10,5,3), while `hand-5` at L@38 is five real digits
 * (19,17,13,11,10). Both read `confidence: low`. A surface that shows the label
 * invites a person to rubber-stamp a phantom finger — which is exactly how the
 * kobold got one, and the reason §8 puts a human here at all.
 *
 * So the evidence is the PERSISTENCE PROFILE and the branch's position in the
 * world, and this module's whole job is to carry both out of voxel space where
 * a viewport can draw them and a hand can click them.
 *
 * It measures; it never decides.
 *
 * THAT LAST RULE WAS AMENDED, AND BY WHOM MATTERS. Until 2026-08-11 this header
 * said there was no `suggestedThumb` here and there never must be — an
 * analyzer-shaped guess beside a pick button is a default wearing a question's
 * clothes, and people confirm defaults. ADR-022 **D4** overrules that, on
 * Allen's field evidence after dozens of grafts: *"as long as pre-fill can be
 * re-assigned, we should do it."* {@link HandEvidence.thumbProposal} is that
 * amendment, and it carries the conditions the old rule was protecting:
 *
 *  - It is the analyzer's §4 pre-fill, which is MARGIN-GUARDED at the source —
 *    `thumbPrefill` requires a ≥2-level parting margin AND divergence past the
 *    direction-uncertainty bound, and returns null rather than guess. A weak
 *    hand still arrives here with nothing suggested.
 *  - A surface consuming it must show a proposal AS a proposal, visually
 *    distinct from a person's own answer (§8, the `withAutoSetAside` precedent),
 *    and must leave it re-assignable in one gesture.
 *  - It is never a placement input. Placement takes pins (ADR-022 §3), and a
 *    proposal that nobody accepted pins nothing.
 *
 * The old rule was right about the danger and wrong about the cure: the cure is
 * a proposal a person can see through, not an empty panel that makes them hunt.
 */
import { isolateHandRegion, soleSkinnedMesh, findBoneByName } from '@harryhausen/adapter-three';
import {
  analyzeSeparationProfile,
  limbAxisCentre,
  voxelPointToWorld,
  voxelizeMesh,
  robustCutCentre,
  type VoxelGrid,
} from '@harryhausen/kernel';
import { Matrix4, Vector3 } from 'three';
import type { Object3D } from 'three';
import { bindPoseVertices } from './elbow-evidence.js';
import type { HandCandidate } from './hand-candidates.js';

export interface Vec3 {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

export interface BranchEvidence {
  /** `branch-N` — the id a confirmation records, so it must round-trip exactly. */
  readonly id: string;
  /**
   * Voxels this branch survives as a separate component. THE discriminator: a
   * digit persists, a surface bump does not, and the GAP between the smallest
   * real digit and the largest bump is what a person is really reading.
   */
  readonly persistenceVoxels: number;
  /**
   * Level at which it parted from the palm mass. A thumb leaves early — on the
   * android's left hand, 7 against 18–25 for the four fingers. Shown because it
   * is the honest cue, NOT applied as a rule.
   */
  readonly baseLevel: number;
  /** Where it starts and ends, in WORLD space — the marker's anchor. */
  readonly baseWorld: Vec3;
  readonly tipWorld: Vec3;
  /** Straight-line extent, world units. Thumbs are usually shorter. */
  readonly lengthWorld: number;
  /**
   * The WHOLE medial path base→tip, in world space — not just the endpoints.
   * A surface needs this to show which part of the mesh a branch actually IS:
   * a line between two points says "somewhere over there", while the path plus
   * the region below lets the vertices themselves light up.
   */
  readonly chainWorld: readonly Vec3[];
}

export interface HandEvidence {
  readonly handBone: string;
  readonly side: 'left' | 'right';
  readonly resolution: number;
  /** The analyzer's classification and confidence — reported, never leading. */
  readonly profile: string;
  readonly confidence: string;
  readonly branches: readonly BranchEvidence[];
  /**
   * The analyzer's §4 thumb pre-fill at THIS resolution — a branch id, or null
   * when the volume could not separate one within its own margins. The pin
   * surface's whole pre-fill hangs off this one anchor: with a thumb, the
   * remaining digits order along the knuckle row and can be named
   * (`proposeNamesForTips`); without one, nothing is proposed and a person
   * names their own pins. See the header for the D4 amendment that allows it
   * here at all, and the conditions that come with it.
   */
  readonly thumbProposal: string | null;
  /** Voxel count of the isolated region — the sanity number under everything. */
  readonly occupiedVoxels: number;
  /**
   * The isolated hand's own vertices, bind-world, flat `[x,y,z,…]`, with the
   * voxel edge length they were measured at. Carried so a surface can paint the
   * MEASURED GEOMETRY rather than a symbol standing in for it — the difference
   * between "branch-4 persists 5" and seeing five voxels' worth of cuff rim
   * light up at the wrist.
   */
  readonly region: {
    readonly positions: Float32Array;
    readonly voxelSize: number;
    /**
     * The MESH vertex index of each region vertex, so a click can be tested for
     * membership exactly rather than by distance. A point-to-nearest-vertex
     * threshold cannot tell "off the hand" from "in the middle of a big
     * triangle" — measured on the goblin, where a click dead on a fingertip
     * sits 6 mm from the nearest vertex of a low-poly hand and a 2-voxel margin
     * refused it. Membership has no threshold to get wrong.
     */
    readonly vertexIndices: Uint32Array;
  };
  /**
   * The wrist joint vs the wrist VOLUME (bind-world): generated rigs place the
   * joint in front of the flesh, and this is the measured evidence the
   * recenter proposal shows (unbind-adjust-rebind #1, 2026-07-28). The
   * centroid is the wrist cut's centre — where the recenter would put the bind.
   */
  readonly wrist: { readonly boneWorld: Vec3; readonly centroidWorld: Vec3; readonly offsetWorld: number };
}

/** A hand region that produced no analyzable volume — a finding, said out loud. */
export class HandEvidenceRefusal extends Error {}

const dist = (a: Vec3, b: Vec3): number => Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z);

/**
 * Measure one hand at one resolution. Called again on every resolution change —
 * it is cheap relative to a graft, and watching branches appear and merge as the
 * knob moves is the interaction, not a side effect of it.
 */
export function measureHand(scene: Object3D, candidate: HandCandidate, resolution: number): HandEvidence {
  const mesh = soleSkinnedMesh(scene);
  const handBone = findBoneByName(mesh, candidate.handBone);
  if (!handBone) {
    throw new HandEvidenceRefusal(
      `hand "${candidate.handBone}" has no scene node — the rig and the scene disagree (§8: named, not swallowed).`,
    );
  }
  const forearm = candidate.forearmBone === null ? null : findBoneByName(mesh, candidate.forearmBone);
  const iso = isolateHandRegion(mesh, handBone, forearm, {});
  const grid: VoxelGrid = voxelizeMesh(iso.positions, iso.indices, { cutPlane: iso.cutPlane, resolution });
  const analysis = analyzeSeparationProfile(grid, {});

  if (analysis.kind !== 'hand') {
    throw new HandEvidenceRefusal(
      `hand "${candidate.handBone}" @ res ${resolution}: the volume analyzed as '${analysis.kind}', not a hand — ` +
        `nothing to pin here. Move the resolution, or the hand bone is wrong.`,
    );
  }

  // Bind-world wrist joint (IBM-derived — the pose-independent truth) vs the
  // wrist cut centroid: the recenter proposal's measured evidence.
  const boneIndex = mesh.skeleton.bones.indexOf(handBone);
  const ibm = mesh.skeleton.boneInverses[boneIndex];
  const boneWorldV =
    ibm !== undefined ? new Vector3().setFromMatrixPosition(new Matrix4().copy(ibm).invert()) : new Vector3();
  const boneWorld = { x: boneWorldV.x, y: boneWorldV.y, z: boneWorldV.z };
  // The RAW-VERTS flesh centre at the wrist station (the limb-axis capture) —
  // the panel's mm and the graft's recenter must be the SAME number, and the
  // fox pair proved isolation-derived middles inherit skin-weight bias (the
  // hand-weighted region can be the anterior third of the visible tube).
  // The wrist mm is AUXILIARY evidence: a capture refusal degrades to the
  // voxel-slab centre and must never take the digit offer down with it
  // (measured 2026-08-02: a fatal limb-axis refusal blanked the whole panel).
  let centroidWorld;
  try {
    if (forearm === null || forearm === undefined) throw new HandEvidenceRefusal('no forearm — voxel fallback');
    const fIndex = mesh.skeleton.bones.indexOf(forearm);
    const fIbm = mesh.skeleton.boneInverses[fIndex];
    if (fIbm === undefined) throw new HandEvidenceRefusal('forearm has no IBM — voxel fallback');
    const fWorldV = new Vector3().setFromMatrixPosition(new Matrix4().copy(fIbm).invert());
    const axis = { x: boneWorld.x - fWorldV.x, y: boneWorld.y - fWorldV.y, z: boneWorld.z - fWorldV.z };
    const len = Math.hypot(axis.x, axis.y, axis.z);
    centroidWorld = limbAxisCentre(
      bindPoseVertices(scene),
      boneWorld,
      { x: axis.x / len, y: axis.y / len, z: axis.z / len },
      len,
    ).centre;
  } catch {
    centroidWorld = voxelPointToWorld(grid, robustCutCentre(grid, iso.cutPlane).centre);
  }

  return {
    handBone: candidate.handBone,
    side: candidate.side,
    resolution,
    profile: analysis.profile,
    confidence: analysis.confidence,
    thumbProposal: analysis.thumb,
    occupiedVoxels: grid.occupied.size,
    region: { positions: iso.positions, voxelSize: grid.voxelSize, vertexIndices: iso.vertexIndices },
    wrist: { boneWorld, centroidWorld, offsetWorld: dist(boneWorld, centroidWorld) },
    branches: analysis.branches.map((br) => {
      const base = voxelPointToWorld(grid, br.chain[0]!);
      const tip = voxelPointToWorld(grid, br.chain[br.chain.length - 1]!);
      return {
        id: br.id,
        persistenceVoxels: br.persistenceVoxels,
        baseLevel: br.baseLevel,
        baseWorld: base,
        tipWorld: tip,
        lengthWorld: dist(base, tip),
        chainWorld: br.chain.map((p) => voxelPointToWorld(grid, p)),
      };
    }),
  };
}

/**
 * The gaps between consecutive persistences, largest first — the STRUCTURE of
 * the reading, for a surface to draw. Not a boundary finder, and the difference
 * matters enough to state: I expected the largest gap to be the digit/bump
 * cliff, measured it, and it is not.
 *
 * The android's right hand at 40 reads 22,13,10,5,3. The biggest gap is 9, from
 * 22 to 13 — that is the THUMB standing apart from the fingers. The digit/bump
 * cliff is the smaller 10→5. Two different features of one hand, and no rule
 * that reads "take the largest gap" can tell them apart; on another hand the
 * same number means the other thing.
 *
 * Which is the argument for the human, made in data rather than in prose: the
 * structure is legible at a glance and not decidable by a threshold.
 */
export function persistenceGaps(evidence: HandEvidence): readonly { readonly afterIndex: number; readonly drop: number }[] {
  const p = evidence.branches.map((b) => b.persistenceVoxels);
  return p
    .slice(0, -1)
    .map((v, i) => ({ afterIndex: i, drop: v - p[i + 1]! }))
    .sort((a, b) => b.drop - a.drop);
}
